import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Modal, TextField, Switch,
  IconButton, Avatar, FormControl, InputLabel, Select, MenuItem, Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PowerIcon from '@mui/icons-material/Power';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import axios from 'axios';

const TTS_LANGUAGES = [
  { code: 'tr-TR', label: 'Türkçe' },
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-GB', label: 'English (UK)' },
  { code: 'de-DE', label: 'Deutsch' },
  { code: 'fr-FR', label: 'Français' },
  { code: 'it-IT', label: 'Italiano' },
  { code: 'es-ES', label: 'Español' },
  { code: 'pt-BR', label: 'Português (Brasil)' },
  { code: 'ar-SA', label: 'العربية' },
  { code: 'ru-RU', label: 'Русский' },
  { code: 'ja-JP', label: '日本語' },
  { code: 'ko-KR', label: '한국어' },
  { code: 'cmn-CN', label: '中文' },
  { code: 'hi-IN', label: 'हिन्दी' }
];

const getEmptyFormData = () => ({
  id: null,
  name: '',
  description: '',
  avatar: null,
  educationIds: [],
  prompts: [],
  isActive: true,
  ttsLanguage: 'tr-TR'
});

export default function AdminBotsPage() {
  const [bots, setBots] = useState([]);
  const [educationPackages, setEducationPackages] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [preview, setPreview] = useState(null);

  const activeCount = bots.filter(b => b.isActive).length;
  const passiveCount = bots.filter(b => !b.isActive).length;

  const [formData, setFormData] = useState(getEmptyFormData());

  const getAuthHeaders = useCallback(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchBots = useCallback(async () => {
    const res = await axios.get("/api/bots", {
      headers: getAuthHeaders()
    });
    setBots(res.data);
  }, [getAuthHeaders]);

  const fetchEducations = useCallback(async () => {
    const res = await axios.get("/api/education", {
      headers: getAuthHeaders()
    });
    setEducationPackages(res.data);
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchBots();
    fetchEducations();
  }, [fetchBots, fetchEducations]);

  const handleSave = async () => {
    const form = new FormData();
    form.append('name', formData.name);
    form.append('description', formData.description);
    if (formData.avatar) form.append('avatar', formData.avatar);
    form.append('educationIds', JSON.stringify(formData.educationIds));
    form.append('prompts', JSON.stringify(formData.prompts));
    form.append('isActive', formData.isActive);
    form.append('ttsLanguage', formData.ttsLanguage);

    if (formData.id) {
      await axios.put(`/api/bots/${formData.id}`, form, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
    } else {
      await axios.post("/api/bots", form, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
    }

    fetchBots();
    setFormData(getEmptyFormData());
    setPreview(null);
    setOpenModal(false);
  };

  const handleEdit = (bot) => {
    setFormData({
      id: bot._id,
      name: bot.name || '',
      description: bot.description || '',
      avatar: null,
      educationIds: (bot.educationContents || []).map(e => e._id),
      prompts: bot.prompts || [],
      isActive: bot.isActive ?? true,
      ttsLanguage: bot.ttsLanguage || 'tr-TR',
    });
    setPreview(bot.avatarUrl || bot.avatar || null);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu botu silmek istiyor musun?')) return;
    await axios.delete(`/api/bots/${id}`, {
      headers: getAuthHeaders()
    });
    fetchBots();
  };

  const columns = [
    { field: 'name', headerName: 'Bot Adı', flex: 0.5 },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1
    },
    {
      field: 'avatar',
      headerName: 'Avatar',
      flex: 0.5,
      renderCell: (params) => (
        <Avatar
          alt={params.row.name}
          src={params.row.avatar}
        />
      )
    },
    {
      field: 'educationContents',
      headerName: 'Eğitim Paketleri',
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(params.value || []).map((ep, i) => (
            <Chip
              key={i}
              size="small"
              label={typeof ep === 'string' ? ep : ep?.title || 'Bilinmeyen'}
            />
          ))}
        </Box>
      )
    },
    {
      field: 'ttsLanguage',
      headerName: 'TTS Dili',
      flex: 0.6,
      renderCell: (params) => (
        <Chip size="small" label={params.value || 'tr-TR'} />
      )
    },
    {
      field: 'isActive',
      headerName: 'Durum',
      flex: 0.5,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <CheckCircleIcon /> : <HighlightOffIcon />}
          label={params.value ? 'Aktif' : 'Pasif'}
          color={params.value ? 'success' : 'warning'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'İşlemler',
      flex: 0.5,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => handleEdit(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(params.row._id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', gap: 3, mb: 3, justifyContent: 'center' }}>
        <Card sx={{ width: 250, textAlign: 'center', boxShadow: 3 }}>
          <CardContent>
            <PowerIcon sx={{ fontSize: 40, color: 'green' }} />
            <Typography variant="subtitle2">Aktif Botlar</Typography>
            <Typography variant="h5">{activeCount}</Typography>
          </CardContent>
        </Card>

        <Card sx={{ width: 250, textAlign: 'center', boxShadow: 3 }}>
          <CardContent>
            <PauseCircleIcon sx={{ fontSize: 40, color: '#fbc02d' }} />
            <Typography variant="subtitle2">Pasif Botlar</Typography>
            <Typography variant="h5">{passiveCount}</Typography>
          </CardContent>
        </Card>

        <Card sx={{ width: 250, textAlign: 'center', boxShadow: 3 }}>
          <CardContent>
            <SmartToyIcon sx={{ fontSize: 40, color: '#757575' }} />
            <Typography variant="subtitle2">Toplam Bot</Typography>
            <Typography variant="h5">{bots.length}</Typography>
          </CardContent>
        </Card>
      </Box>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => {
          setFormData(getEmptyFormData());
          setPreview(null);
          setOpenModal(true);
        }}
        sx={{ mb: 2 }}
      >
        Yeni Bot Ekle
      </Button>

      <Box sx={{ width: '97%', overflowX: 'auto' }}>
        <div style={{ minWidth: 900, height: 500 }}>
          <DataGrid
            rows={bots.map(bot => ({ id: bot._id, ...bot }))}
            columns={columns}
            pageSize={10}
            autoHeight
            getRowHeight={() => 80}
            sx={{
              '& .MuiDataGrid-cell': {
                alignItems: 'center',
                display: 'flex'
              }
            }}
          />
        </div>
      </Box>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            p: 4,
            bgcolor: 'white',
            mx: 'auto',
            my: '5%',
            width: 600,
            borderRadius: 2,
            position: 'relative',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => setOpenModal(false)}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" gutterBottom>
            {formData.id ? 'Bot Düzenle' : 'Yeni Bot Ekle'}
          </Typography>

          <TextField
            label="Bot Adı"
            fullWidth
            sx={{ mb: 2, mt: 2 }}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <TextField
            label="Açıklama (Max 150 karakter)"
            fullWidth
            multiline
            inputProps={{ maxLength: 150 }}
            sx={{ mb: 2 }}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>TTS Dili</InputLabel>
            <Select
              value={formData.ttsLanguage}
              label="TTS Dili"
              onChange={(e) => setFormData({ ...formData, ttsLanguage: e.target.value })}
            >
              {TTS_LANGUAGES.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {preview ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar src={preview} sx={{ width: 56, height: 56 }} />
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  setFormData({ ...formData, avatar: null });
                  setPreview(null);
                }}
              >
                Resmi Kaldır
              </Button>
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Button
                startIcon={<UploadFileIcon />}
                component="label"
                variant="outlined"
                fullWidth
              >
                Avatar Yükle
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0];
                    if (!selectedFile) return;
                    setFormData({ ...formData, avatar: selectedFile });
                    setPreview(URL.createObjectURL(selectedFile));
                  }}
                />
              </Button>
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Eğitim Paketleri</InputLabel>
            <Select
              multiple
              value={formData.educationIds}
              onChange={(e) => setFormData({ ...formData, educationIds: e.target.value })}
              renderValue={(selected) =>
                selected
                  .map(id => {
                    const item = educationPackages.find(ep => ep._id === id);
                    return item ? item.title : id;
                  })
                  .join(', ')
              }
            >
              {educationPackages.map((ep) => (
                <MenuItem key={ep._id} value={ep._id}>
                  {ep.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
            Temel Promptlar
          </Typography>

          {formData.prompts.map((p, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                value={p}
                onChange={(e) => {
                  const copy = [...formData.prompts];
                  copy[index] = e.target.value;
                  setFormData({ ...formData, prompts: copy });
                }}
              />
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  const copy = [...formData.prompts];
                  copy.splice(index, 1);
                  setFormData({ ...formData, prompts: copy });
                }}
              >
                Sil
              </Button>
            </Box>
          ))}

          <Button
            variant="outlined"
            sx={{ mb: 2 }}
            onClick={() =>
              setFormData({ ...formData, prompts: [...formData.prompts, ''] })
            }
          >
            + Prompt Ekle
          </Button>

          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
            <Typography>Aktif mi?</Typography>
            <Switch
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
            />
          </Box>

          <Button variant="contained" fullWidth onClick={handleSave}>
            Kaydet
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}